import { useMemo } from 'react';
import type { PrintOrientationTypeEnum } from 'venn-api';
import { getPDF, getTaxRateSettings } from 'venn-api';
import {
  UrlParam,
  addUrlParam,
  analyticsService,
  assertNotNil,
  logExceptionIntoSentry,
  useDebugValue,
  useHasFF,
  PageType,
  getContext as getContextFromTree,
} from 'venn-utils';
import { Notifications, NotificationType } from 'venn-ui-kit';
import { useRecoilCallback, useRecoilValue, waitForAll } from 'recoil';
import {
  allBlockIdsState,
  allProxyInfoState,
  allSubjects,
  allViewSubjectIds,
  analysisSubjectQuery,
  analysisViewIdState,
  analysisViewNameState,
  analysisViewOwnerContextIdState,
  availableContextsState,
  blockCustomMetricSettingsState,
  blockMetrics,
  blockScenarios,
  blockSettings,
  isReportState,
  requestSpecificSubjects,
  studioPrintOrientationType,
  viewPages,
  viewTaxRatesPerSubject,
} from 'venn-state';
import { useStudioExcelExport } from './useStudioExcelExport';
import { buildComplianceMetadata } from './buildComplianceMetadata';
import { sumBy, zip } from 'lodash';

function writeToFile(data: BlobPart, name: string) {
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = window.URL?.createObjectURL?.(blob);
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

const useStudioExport = () => {
  const debugPageState = useDebugValue('debugPrintPage');
  const hasComplianceMetadataFF = useHasFF('compliance_metadata_export_ff');
  const isReport = useRecoilValue(isReportState);

  const logExport = useRecoilCallback(
    ({ snapshot }) =>
      async (
        props:
          | { exportTo: 'Excel'; isSuccessful: boolean; blockCount: number }
          | { exportTo: 'PDF'; printOrientation: PrintOrientationTypeEnum; isSuccessful: boolean; isFinal: boolean },
      ) => {
        const viewId = await snapshot.getPromise(analysisViewIdState);
        const isReport = await snapshot.getPromise(isReportState);
        const blockIds = await snapshot.getPromise(allBlockIdsState);
        const allProxyInfo = await snapshot.getPromise(allProxyInfoState);
        const containsProxies = Object.values(allProxyInfo)
          .flat()
          .some((x) => x);
        const pages = await snapshot.getPromise(viewPages);
        const containsDisclosurePage = pages.some((page) => page.type === PageType.DISCLOSURE);
        const blockInfos = zip(
          blockIds,
          await snapshot.getPromise(waitForAll(blockIds.map((id) => blockSettings(id)))),
          await snapshot.getPromise(waitForAll(blockIds.map((id) => blockCustomMetricSettingsState(id)))),
        ).map(([blockId, blockSetting, customMetrics]) => ({
          blockId: assertNotNil(blockId),
          blockSetting: assertNotNil(blockSetting),
          customMetrics: assertNotNil(customMetrics),
          blockType: assertNotNil(blockSetting).customBlockType,
        }));

        const numPortfolioComparisonBlocks = sumBy(
          blockInfos,
          ({ blockType }) => +(blockType === 'PORTFOLIO_COMPARISON'),
        );
        const numPortfolioComparisonBlocksWithCustomMetrics = sumBy(
          blockInfos,
          ({ customMetrics, blockType }) => +(customMetrics.length > 0 && blockType === 'PORTFOLIO_COMPARISON'),
        );
        const allScenarios = (await snapshot.getPromise(waitForAll(blockIds.map((id) => blockScenarios(id))))).flat();
        const allMetrics = (await snapshot.getPromise(waitForAll(blockIds.map((id) => blockMetrics(id))))).flat();
        const logFunction = isReport ? analyticsService.reportLabExport : analyticsService.studioExport;

        const subjectIds = await snapshot.getPromise(allViewSubjectIds);
        logFunction({
          blockCount: blockIds.length,
          subjectIds,
          viewId,
          blocks: blockInfos.map(({ blockType }) => blockType),
          scenarioFundNames: allScenarios.map((s) => s.fundName),
          containsMacroScenarios: allScenarios.some((s) => s.type === 'MACRO'),
          containsMarketScenarios: allScenarios.some((s) => s.type === 'MARKET'),
          containsIncomeMetric: allMetrics.some((m) => m === 'income'),
          containsYieldMetric: allMetrics.some((m) => m === 'percentageYield'),
          containsProxies,
          containsDisclosurePage,
          numPortfolioComparisonBlocks,
          numPortfolioComparisonBlocksWithCustomMetrics,
          ...props,
        });
      },
    [],
  );

  const onExport = useRecoilCallback(
    ({ snapshot }) =>
      async (isInternal: boolean, analysisViewName?: string, analysisViewId?: string) => {
        const viewId = await snapshot.getPromise(analysisViewIdState);
        const name = await snapshot.getPromise(analysisViewNameState);
        const ownerContextId = await snapshot.getPromise(analysisViewOwnerContextIdState);
        /** isFinal is the opposite of isInternal; internal reports are not 'final'. */
        const isFinal = !isInternal;
        const filename = `${name ?? 'report'}.pdf`;
        let isSuccessful = true;
        const toastId = Notifications.notify(`Downloading '${filename}'`, NotificationType.LOADING);
        const printOrientationType = await snapshot.getPromise(studioPrintOrientationType);
        const urlParams = new URLSearchParams();
        urlParams.append('print', 'true');
        urlParams.append('internal', String(isInternal));
        debugPageState?.enabledOnPrint && addUrlParam(urlParams, UrlParam.PRINT_DEBUG, debugPageState);
        const restUrlParams = `&${urlParams.toString()}`;
        try {
          let complianceMetadata;
          if (isFinal && isReport && hasComplianceMetadataFF) {
            const subjects = await snapshot.getPromise(allSubjects);
            const resolvedAnalysisSubjects = await snapshot.getPromise(
              waitForAll(subjects.map((s) => analysisSubjectQuery(s))),
            );
            const resolvedRequestSubjects = await snapshot.getPromise(requestSpecificSubjects(subjects));
            const taxRatesPerSubject_ = await snapshot.getPromise(viewTaxRatesPerSubject);
            const contexts = await snapshot.getPromise(availableContextsState);
            const workspaceDefaultTaxRate = await getTaxRateSettings()
              .then((res) => res.content)
              .catch(() => undefined);
            complianceMetadata = await buildComplianceMetadata({
              subjects,
              analysisSubjects: resolvedAnalysisSubjects,
              requestSubjects: resolvedRequestSubjects,
              taxRatesPerSubject: taxRatesPerSubject_,
              workspaceDefaultTaxRate,
              getContext: (contextId) => (contexts ? getContextFromTree(contextId, contexts) : undefined),
              viewOwnerContextId: ownerContextId,
            });
          }

          const response = await getPDF({
            printOrientationType,
            url:
              window.location.hostname === 'localhost'
                ? `https://dev.tsvenn.com${window.location.pathname}${window.location.search}${restUrlParams}`
                : `${window.location.href}${restUrlParams}`,
            final: isFinal,
            ownerContextId,
            name: analysisViewName ?? name,
            viewId: analysisViewId ?? viewId,
            complianceMetadata: complianceMetadata ?? null,
          });

          writeToFile(response.content, filename);
          Notifications.notifyUpdate(toastId, `Downloaded '${filename}'`, NotificationType.SUCCESS);
        } catch (exception) {
          isSuccessful = false;
          Notifications.notifyUpdate(toastId, `Downloading '${filename}' failed`, NotificationType.ERROR);
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          exception && logExceptionIntoSentry(exception);
        } finally {
          logExport({
            exportTo: 'PDF',
            printOrientation: printOrientationType,
            isSuccessful,
            isFinal,
          });
        }
      },
    [debugPageState, logExport, isReport, hasComplianceMetadataFF],
  );

  const onExcelExport = useStudioExcelExport(logExport);

  return useMemo(
    () => ({
      onExport,
      onExcelExport,
    }),
    [onExcelExport, onExport],
  );
};

export default useStudioExport;
